import { useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Switch, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate, useParams } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Tescriptions from '@/components/Tescriptions';
import Editor from '@/components/formItem/Editor';
import ImageUpload from '@/components/formItem/ImageUpload';
import {
  useAddMarketingProduct,
  useUpdateMarketingProduct,
} from '@/hooks/mutations/admin';
import { useAdminProduct } from '@/hooks/queries/admin';
import type { AddMarketingProductRequest } from '@/types/admin';
import { getQueryKey } from '@/utils/queryKeys';

const ProductForm = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isRegister = !id;
  const navigate = useNavigate();
  const [form] = Form.useForm<AddMarketingProductRequest>();
  const query = useAdminProduct(
    {
      id: Number(id),
    },
    { enabled: !isRegister }
  );

  const addMarketingProduct = useAddMarketingProduct({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getProducts'),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProducts'),
      });

      message.success('마케팅 상품을 성공적으로 등록했어요');
      navigate(PATH.MARKETING_PRODUCT_MANAGE.INDEX, { replace: true });
    },
  });
  const updateMarketingProduct = useUpdateMarketingProduct({
    onSuccess: () => {
      message.success('마케팅 상품을 성공적으로 수정했어요');
      navigate(PATH.MARKETING_PRODUCT_MANAGE.INDEX);
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getProduct', { id: Number(id) }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getProducts'),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProduct', { id: Number(id) }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProducts'),
      });
    },
  });

  // 판매가
  const getResultPrice = () =>
    form.getFieldValue('price') - form.getFieldValue('discountPrice');
  // 수익금
  const getMarginPrice = () => getResultPrice() - form.getFieldValue('cost');
  // 수익율
  const getMarginRate = () =>
    (getResultPrice() === 0
      ? 0
      : (getMarginPrice() / getResultPrice()) * 100
    ).toFixed(1);

  if (!isRegister && !query.data) {
    return null;
  }

  const detail = isRegister ? null : query.data;

  return (
    <Form
      css={tw`flex flex-col gap-[32px]`}
      form={form}
      initialValues={
        detail
          ? {
              name: detail.name,
              summary: detail.summary,
              isPublished: detail.isPublished,
              price: detail.price,
              cost: detail.cost,
              discountPrice: detail.discountPrice,
              bannerImageUrl: detail.bannerImageUrl,
              description: detail.description,
              additionalDescription: detail.additionalDescription,
              note: detail.note,
            }
          : {
              name: '',
              summary: '',
              isPublished: true,
              price: 0,
              cost: 0,
              discountPrice: 0,
              bannerImageUrl: '',
              description: '',
              additionalDescription: '',
              note: '',
            }
      }
      onFinish={(values) => {
        if (isRegister) {
          addMarketingProduct.mutate({
            name: values.name,
            summary: values.summary,
            isPublished: values.isPublished,
            price: values.price,
            cost: values.cost,
            discountPrice: values.discountPrice,
            bannerImageUrl: values.bannerImageUrl,
            description: values.description,
            additionalDescription: values.additionalDescription,
            note: values.note,
          });
        } else {
          updateMarketingProduct.mutate({
            id: Number(id),
            summary: values.summary,
            isPublished: values.isPublished,
            price: values.price,
            discountPrice: values.discountPrice,
            cost: values.cost,
            bannerImageUrl: values.bannerImageUrl,
            description: values.description,
            additionalDescription: values.additionalDescription,
            note: values.note,
          });
        }
      }}
      onFinishFailed={() => {
        message.error('입력 항목 확인 후 다시 등록해주세요');
      }}
      scrollToFirstError
    >
      <Tescriptions column={1} title="상품 정보">
        <Tescriptions.Item label="상품명" required>
          {detail ? (
            detail.name
          ) : (
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: '상품명을 입력해주세요',
                },
              ]}
            >
              <Input
                showCount
                maxLength={50}
                placeholder="상품명은 등록 후 수정이 불가능합니다"
              />
            </Form.Item>
          )}
        </Tescriptions.Item>
        <Tescriptions.Item label="상품 설명">
          <Form.Item name="summary">
            <Input
              showCount
              maxLength={100}
              placeholder="상품에 대한 설명을 입력해주세요"
            />
          </Form.Item>
        </Tescriptions.Item>
        <Tescriptions.Item label="게시 여부" required>
          <Form.Item name="isPublished" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="가격 정보" column={2}>
        <Tescriptions.Item label="정가" required>
          <Form.Item
            name="price"
            rules={[
              {
                type: 'number',
                required: true,
                min: 1,
                transform: (value) => Number(value),
                message: '정가를 입력해주세요',
              },
            ]}
          >
            <Input type="number" suffix={<>원</>} />
          </Form.Item>
        </Tescriptions.Item>
        <Tescriptions.Item label="원가" required>
          <Form.Item
            name="cost"
            rules={[
              {
                type: 'number',
                required: true,
                min: 0,
                transform: (value) => (value === '' ? null : Number(value)),
                message: '원가는 0 이상이어야 합니다',
              },
            ]}
          >
            <Input type="number" suffix={<>원</>} />
          </Form.Item>
        </Tescriptions.Item>

        <Tescriptions.Item label="할인가">
          <Form.Item
            name="discountPrice"
            rules={[
              {
                type: 'number',
                required: true,
                min: 0,
                transform: (value) => (value === '' ? null : Number(value)),
                message: '할인가는 0 이상이어야 합니다',
              },
            ]}
          >
            <Input type="number" suffix={<>원</>} />
          </Form.Item>
        </Tescriptions.Item>

        <Tescriptions.Item
          label={
            <>
              수익금
              <span css={tw`font-normal text-[rgba(0, 0, 0, 0.45)]`}>
                (판매가-원가)
              </span>
            </>
          }
        >
          <Form.Item shouldUpdate>
            {() => `${getMarginPrice().toLocaleString()}원`}
          </Form.Item>
        </Tescriptions.Item>
        <Tescriptions.Item
          label={
            <>
              판매가
              <span css={tw`font-normal text-[rgba(0, 0, 0, 0.45)]`}>
                (정가-할인가)
              </span>
            </>
          }
        >
          <Form.Item shouldUpdate>
            {() => `${getResultPrice().toLocaleString()}원`}
          </Form.Item>
        </Tescriptions.Item>
        <Tescriptions.Item label="수익율">
          <Form.Item shouldUpdate>{() => `${getMarginRate()}%`}</Form.Item>
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="상품 상세">
        <Tescriptions.Item label="배너 이미지" required>
          <ImageUpload
            name="bannerImageUrl"
            rules={[
              {
                required: true,
                message: '배너 이미지를 업로드해주세요',
              },
            ]}
            multiple={false}
            initialValue={detail ? detail.bannerImageUrl : undefined}
          />
        </Tescriptions.Item>
        <Tescriptions.Item label="상세 내용" required>
          <Editor
            name="description"
            required
            initialValue={detail ? detail.description : ''}
          />
        </Tescriptions.Item>
        <Tescriptions.Item label="추가 입력 문항">
          <Form.Item name="additionalDescription">
            <Input showCount maxLength={300} />
          </Form.Item>
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="관리 정보">
        <Tescriptions.Item label="내부 관리용 메모">
          <Form.Item name="note">
            <TextArea
              showCount
              maxLength={1000}
              autoSize={{
                minRows: 5,
                maxRows: 20,
              }}
            />
          </Form.Item>
        </Tescriptions.Item>
      </Tescriptions>

      <div css={tw`flex justify-center items-center w-full gap-x-[8px]`}>
        <Button onClick={() => navigate(PATH.MARKETING_PRODUCT_MANAGE.INDEX)}>
          취소
        </Button>
        <Form.Item noStyle>
          <Button type="primary" htmlType="submit">
            {isRegister ? '등록' : '수정'}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default ProductForm;
